import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../hooks/useApi';
import DateInput from '../components/DateInput';
import EnteteBatch from '../components/EnteteBatch';
import EtapeTracabilite from '../components/EtapeTracabilite';
import CoutRevient from '../components/CoutRevient';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import BatchAnalytiquePanel from '../components/BatchAnalytiquePanel';
import Icon from '../components/Icon';
import './ProductionTallow.css';

// ─── CONSTANTES ───────────────────────────────

const ETAPES = [
  { id: 1, label: 'Batch' },
  { id: 2, label: 'Traçabilité' },
  { id: 3, label: 'Fonte' },
  { id: 4, label: 'Fumage' },
  { id: 5, label: 'Finalisation' },
];

// ─── HELPERS ──────────────────────────────────

function fmtMl(ml) {
  if (ml === null || ml === undefined) return '—';
  return ml >= 1000 ? `${(ml / 1000).toFixed(2)} L` : `${ml} ml`;
}

function yieldBadge(pct) {
  if (!pct && pct !== 0) return null;
  if (pct > 90) return { cls: 'badge-danger',  label: `${pct.toFixed(1)}% — trop élevé` };
  if (pct < 30) return { cls: 'badge-danger',  label: `${pct.toFixed(1)}% — trop faible` };
  if (pct >= 50) return { cls: 'badge-success', label: `${pct.toFixed(1)}% — normal` };
  return { cls: 'badge-warning', label: `${pct.toFixed(1)}% — bas` };
}

function calculerYield(volumeMl, poidsCruKg) {
  if (!volumeMl || !poidsCruKg || poidsCruKg <= 0) return null;
  return Math.round((volumeMl / (poidsCruKg * 1000)) * 10000) / 100;
}

// ─── MODAL RECETTE ────────────────────────────

function ModalRecette({ recette, onFermer, onSauvegarde }) {
  const [form, setForm] = useState({
    yield_theorique_pct   : recette?.yield_theorique_pct   ?? 60,
    densite_g_par_ml      : recette?.densite_g_par_ml       ?? 0.92,
    prix_indicatif_par_ml : recette?.prix_indicatif_par_ml  ?? 0,
    notes                 : recette?.notes                  ?? '',
  });
  const [loading, setLoading] = useState(false);
  const [erreur, setErreur]   = useState(null);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setErreur(null);
    try {
      const res = await api.put('/tallow/recette', form);
      onSauvegarde(res.recette);
      onFermer();
    } catch (err) {
      setErreur(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onFermer}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <h3 style={{ fontFamily: "'Cinzel', serif", marginBottom: 20, color: 'var(--text)' }}>
          Modifier la recette Tallow
        </h3>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="form-group">
            <label>Yield théorique (%)</label>
            <input
              className="form-input"
              type="number"
              min="1"
              max="100"
              step="0.1"
              value={form.yield_theorique_pct}
              onChange={e => set('yield_theorique_pct', parseFloat(e.target.value) || 0)}
            />
            <span className="muted" style={{ fontSize: 12 }}>
              Ex : 60 → 10 kg de gras ≈ 6 000 ml de tallow
            </span>
          </div>
          <div className="form-group">
            <label>Densité (g/ml)</label>
            <input
              className="form-input"
              type="number"
              min="0.1"
              max="2"
              step="0.01"
              value={form.densite_g_par_ml}
              onChange={e => set('densite_g_par_ml', parseFloat(e.target.value) || 0)}
            />
            <span className="muted" style={{ fontSize: 12 }}>Tallow de bœuf ≈ 0,92 g/ml</span>
          </div>
          <div className="form-group">
            <label>Prix indicatif par ml ($)</label>
            <input
              className="form-input"
              type="number"
              min="0"
              step="0.001"
              value={form.prix_indicatif_par_ml}
              onChange={e => set('prix_indicatif_par_ml', parseFloat(e.target.value) || 0)}
            />
            <span className="muted" style={{ fontSize: 12 }}>Utilisé dans le calculateur pour estimer le coût</span>
          </div>
          <div className="form-group">
            <label>Notes</label>
            <textarea
              className="form-input"
              rows="3"
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
              placeholder="Notes sur la recette, procédé..."
            />
          </div>
          {erreur && <div className="error-inline"><Icon name="erreur" size={15} />{erreur}</div>}
          <div className="form-actions">
            <button type="button" className="btn btn-ghost" onClick={onFermer}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Sauvegarde...' : 'Sauvegarder'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── STEPPER ──────────────────────────────────

function Stepper({ etape, nbTracabiliteNonLies = 0, onStepClick, hasBatch }) {
  const peutCliquer = (cible) => {
    if (!hasBatch) return cible === 1;
    return true;
  };

  return (
    <div className="stepper">
      {ETAPES.map((e, i) => {
        const clickable = onStepClick && peutCliquer(e.id);
        return (
          <React.Fragment key={e.id}>
            <div
              className={`step ${etape === e.id ? 'active' : etape > e.id ? 'done' : ''} ${clickable ? 'step-cliquable' : ''}`}
              style={{ position: 'relative', cursor: clickable ? 'pointer' : 'default' }}
              onClick={() => clickable && onStepClick(e.id)}
              title={clickable ? `Aller à l'étape ${e.id} — ${e.label}` : ''}
            >
                      <div className="step-circle">
                {etape > e.id ? <Icon name="check" size={14} weight="bold" /> : e.id}
              </div>
              {e.id === 2 && nbTracabiliteNonLies > 0 && (
                <span className="step-badge-warning">{nbTracabiliteNonLies}</span>
              )}
              <span className="step-label">{e.label}</span>
            </div>
            {i < ETAPES.length - 1 && (
              <div className={`step-line ${etape > e.id ? 'done' : ''}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── ÉTAPE 1 — Batch & matière première ───────

function EtapeBatch({ onNext }) {
  const [nomAuto, setNomAuto] = useState('');
  const [nomManuel, setNomManuel] = useState(false);
  const [form, setForm] = useState({
    nom         : '',
    date_prod   : new Date().toISOString().split('T')[0],
    poids_cru_kg: '',
    operateurs  : '',
    heure_debut : '',
    notes       : '',
  });
  const [loading, setLoading] = useState(false);
  const [erreur, setErreur]   = useState(null);
  // Anti-double-soumission synchrone (setLoading n'est pas instantané)
  const submittingRef = useRef(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    api.get('/batches/prochain-numero/tallow')
      .then(r => { setNomAuto(r.nom); set('nom', r.nom); })
      .catch(() => {});
  }, []); // eslint-disable-line

  async function handleSubmit(e) {
    e.preventDefault();
    if (submittingRef.current) return;
    if (!form.poids_cru_kg || parseFloat(form.poids_cru_kg) <= 0) {
      setErreur('Le poids de gras cru (kg) est obligatoire et doit être supérieur à 0.');
      return;
    }
    submittingRef.current = true;
    setLoading(true);
    setErreur(null);
    try {
      const nomEffectif = form.nom.trim() || nomAuto || undefined;
      const res = await api.post('/tallow/batches', {
        nom         : nomEffectif,
        date_prod   : form.date_prod,
        poids_cru_kg: parseFloat(form.poids_cru_kg),
        operateurs  : form.operateurs || undefined,
        heure_debut : form.heure_debut || undefined,
        notes       : form.notes || undefined,
      });
      onNext({ batchId: res.id, form: { ...form, nom: res.nom || nomEffectif, poids_cru_kg: res.poids_cru_kg } });
    } catch (err) {
      setErreur(err.response?.data?.error || err.message);
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  }

  return (
    <form className="tallow-form" onSubmit={handleSubmit}>
      <div className="form-grid">
        <div className="form-group">
          <label>Numéro du batch</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              className="form-input"
              value={form.nom}
              onChange={e => set('nom', e.target.value)}
              readOnly={!nomManuel}
              style={{ flex: 1, opacity: nomManuel ? 1 : 0.75 }}
              placeholder={nomAuto || 'Chargement…'}
            />
            <button type="button" className="btn btn-ghost btn--sm"
              onClick={() => setNomManuel(m => !m)} style={{ whiteSpace: 'nowrap' }}>
              {nomManuel
                ? <><Icon name="retour" size={13} /> Auto</>
                : <><Icon name="modifier" size={13} /> Modifier</>}
            </button>
          </div>
          <span className="muted" style={{ fontSize: 12 }}>Numéro auto-généré (ex: TAL-001)</span>
        </div>
        <div className="form-group">
          <label>Date de production *</label>
          <DateInput
            className="form-input"
            value={form.date_prod}
            onChange={v => set('date_prod', v)}
          />
        </div>
      </div>

      <div className="form-grid">
        <div className="form-group">
          <label>Poids de gras cru (kg) *</label>
          <input
            className="form-input"
            type="number"
            min="0.1"
            step="0.1"
            value={form.poids_cru_kg}
            onChange={e => set('poids_cru_kg', e.target.value)}
            placeholder="Ex : 10.0"
            required
          />
          <span className="muted" style={{ fontSize: 12 }}>
            Poids du fat trimming solide avant la fonte
          </span>
        </div>
        <div className="form-group">
          <label>Opérateurs *</label>
          <select
            className="form-input"
            value={form.operateurs}
            onChange={e => set('operateurs', e.target.value)}
          >
            <option value="">— Sélectionner —</option>
            <option value="LCU">LCU</option>
            <option value="ELA">ELA</option>
            <option value="LCU+ELA">Les deux</option>
          </select>
        </div>
      </div>

      <div className="form-grid">
        <div className="form-group">
          <label>Heure de début</label>
          <input
            className="form-input"
            type="time"
            value={form.heure_debut}
            onChange={e => set('heure_debut', e.target.value)}
          />
        </div>
        <div className="form-group">{/* placeholder pour aligner le grid */}</div>
      </div>

      <div className="form-group">
        <label>Notes</label>
        <textarea
          className="form-input"
          rows="2"
          value={form.notes}
          onChange={e => set('notes', e.target.value)}
          placeholder="Notes de production, qualité du gras, provenance..."
        />
      </div>

      {erreur && <div className="error-inline"><Icon name="erreur" size={15} />{erreur}</div>}

      <div className="form-actions">
        <button
          type="submit"
          className="btn btn-primary"
          disabled={loading || !form.date_prod || !form.poids_cru_kg}
        >
          {loading ? 'Création...' : 'Créer le batch'}
        </button>
      </div>
    </form>
  );
}

// ─── ÉTAPE 3 — Fonte ──────────────────────────

function EtapeFonte({ batchData, onNext, onBack }) {
  const [form, setForm] = useState({
    temp_fonte_c : batchData?.batch?.temp_fonte_c ?? '',
    heure_debut  : batchData?.batch?.heure_debut  ?? '',
    heure_fin    : batchData?.batch?.heure_fin    ?? '',
  });
  const [loading, setLoading] = useState(false);
  const [erreur, setErreur]   = useState(null);
  const [succes, setSucces]   = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setErreur(null);
    try {
      await api.put(`/tallow/batches/${batchData.batchId}/cuisson`, {
        temp_fonte_c : form.temp_fonte_c !== '' ? parseFloat(form.temp_fonte_c) : undefined,
        heure_debut  : form.heure_debut || undefined,
        heure_fin    : form.heure_fin   || undefined,
      });
      setSucces(true);
      setTimeout(() => onNext(), 600);
    } catch (err) {
      setErreur(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <form className="tallow-form" onSubmit={handleSubmit}>
      <div className="tallow-info-box">
        <strong>Fonte du gras</strong> — Chauffer à feu doux/moyen jusqu'à fonte complète.
        Recommandation : 100–120 °C. Écumer les impuretés en surface.
      </div>

      <div className="form-grid">
        <div className="form-group">
          <label>Température de fonte atteinte (°C)</label>
          <input
            className="form-input"
            type="number"
            min="50"
            max="200"
            step="0.5"
            value={form.temp_fonte_c}
            onChange={e => set('temp_fonte_c', e.target.value)}
            placeholder="Ex : 110"
          />
          <span className="muted" style={{ fontSize: 12 }}>
            Recommandation : 100–120 °C
          </span>
        </div>
        <div className="form-group">
          {/* colonne vide pour alignement */}
        </div>
      </div>

      <div className="form-grid">
        <div className="form-group">
          <label>Heure début de la fonte</label>
          <input
            className="form-input"
            type="time"
            value={form.heure_debut}
            onChange={e => set('heure_debut', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Heure fin de la fonte</label>
          <input
            className="form-input"
            type="time"
            value={form.heure_fin}
            onChange={e => set('heure_fin', e.target.value)}
          />
        </div>
      </div>

      {succes && <div className="alert-inline-success"><Icon name="succes" size={15} />Fonte enregistrée</div>}
      {erreur  && <div className="error-inline"><Icon name="erreur" size={15} />{erreur}</div>}

      <div className="form-actions">
        <button type="button" className="btn btn-ghost" onClick={onBack}><Icon name="retour" size={14} /> Retour</button>
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Enregistrement...' : 'Enregistrer la fonte'}
        </button>
      </div>
    </form>
  );
}

// ─── ÉTAPE 4 — Fumage & Filtration ────────────

function EtapeFumage({ batchData, onNext, onBack }) {
  const [form, setForm] = useState({
    heure_debut_fumage : batchData?.batch?.heure_debut_fumage ?? '',
    heure_fin_fumage   : batchData?.batch?.heure_fin_fumage   ?? '',
    notes_filtration   : batchData?.batch?.notes_filtration   ?? '',
  });
  const [loading, setLoading] = useState(false);
  const [erreur, setErreur]   = useState(null);
  const [succes, setSucces]   = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setErreur(null);
    try {
      await api.put(`/tallow/batches/${batchData.batchId}/fumage`, {
        heure_debut_fumage : form.heure_debut_fumage || undefined,
        heure_fin_fumage   : form.heure_fin_fumage   || undefined,
        notes_filtration   : form.notes_filtration   || undefined,
      });
      setSucces(true);
      setTimeout(() => onNext(), 600);
    } catch (err) {
      setErreur(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <form className="tallow-form" onSubmit={handleSubmit}>
      <div className="tallow-info-box">
        <strong>Fumage & Filtration</strong> — Fumer le tallow fondu pour lui donner ses arômes
        caractéristiques, puis filtrer pour retirer toutes les impuretés solides.
        Filtrer à travers une étamine ou filtre à café en métal.
      </div>

      <div className="form-grid">
        <div className="form-group">
          <label>Heure début du fumage</label>
          <input
            className="form-input"
            type="time"
            value={form.heure_debut_fumage}
            onChange={e => set('heure_debut_fumage', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Heure fin du fumage</label>
          <input
            className="form-input"
            type="time"
            value={form.heure_fin_fumage}
            onChange={e => set('heure_fin_fumage', e.target.value)}
          />
        </div>
      </div>

      <div className="form-group">
        <label>Notes de filtration</label>
        <textarea
          className="form-input"
          rows="3"
          value={form.notes_filtration}
          onChange={e => set('notes_filtration', e.target.value)}
          placeholder="Limpidité, couleur, odeur, observations sur la filtration..."
        />
      </div>

      {succes && <div className="alert-inline-success"><Icon name="succes" size={15} />Fumage et filtration enregistrés</div>}
      {erreur  && <div className="error-inline"><Icon name="erreur" size={15} />{erreur}</div>}

      <div className="form-actions">
        <button type="button" className="btn btn-ghost" onClick={onBack}><Icon name="retour" size={14} /> Retour</button>
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Enregistrement...' : 'Enregistrer fumage & filtration'}
        </button>
      </div>
    </form>
  );
}

// ─── ÉTAPE 5 — Volume final & Finalisation ────

function EtapeFinalisation({ batchData, recette, onSuccess, onBack }) {
  const [form, setForm] = useState({
    volume_tallow_ml       : '',
    date_congelation       : '',
    date_expiration_produit: '',
    temp_conservation      : 'ambiante',
    incident               : '',
  });
  const [calculateur, setCalculateur]   = useState(null);
  const [apercuDeduction, setApercuDeduction] = useState(null);
  const [loading, setLoading]           = useState(false);
  const [erreur, setErreur]             = useState(null);
  const [resultat, setResultat]         = useState(null);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const poidsCruKg = parseFloat(batchData?.form?.poids_cru_kg) || 0;

  // Charger calculateur et aperçu déduction au montage
  useEffect(() => {
    if (poidsCruKg > 0) {
      api.get(`/tallow/calculateur?poids_kg=${poidsCruKg}`)
        .then(setCalculateur)
        .catch(() => {});
    }
    if (batchData?.batchId) {
      api.get(`/tallow/batches/${batchData.batchId}/apercu-deduction`)
        .then(setApercuDeduction)
        .catch(() => {});
    }
  }, []); // eslint-disable-line

  const volumeMl  = parseFloat(form.volume_tallow_ml) || 0;
  const yieldReel = calculerYield(volumeMl, poidsCruKg);
  const badge     = yieldBadge(yieldReel);

  const isFinalise = batchData?.form?.statut === 'finalise';

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.volume_tallow_ml || volumeMl <= 0) {
      setErreur('Le volume de tallow produit (ml) est obligatoire et doit être supérieur à 0.');
      return;
    }
    setLoading(true);
    setErreur(null);
    try {
      const res = await api.post(`/tallow/batches/${batchData.batchId}/production`, {
        volume_tallow_ml       : volumeMl,
        date_congelation       : form.date_congelation        || undefined,
        date_expiration_produit: form.date_expiration_produit || undefined,
        temp_conservation      : form.temp_conservation       || undefined,
        incident               : form.incident                || undefined,
      });
      setResultat(res);
      onSuccess(res);
    } catch (err) {
      setErreur(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }

  if (resultat) {
    const b = yieldBadge(resultat.yield_tallow_pct);
    return (
      <div className="resultat-ok rise-in">
        <div className="resultat-icon">
          <Icon name="succes" size={26} />
        </div>
        <h3>Batch finalisé avec succès !</h3>

        <div className="resultat-grid">
          <div className="stat-box">
            <div className="stat-val gold">{fmtMl(resultat.volume_tallow_ml)}</div>
            <div className="stat-lbl">Tallow produit</div>
          </div>
          <div className="stat-box">
            <div className="stat-val" style={{ fontSize: 14 }}>
              <span className={`badge ${b?.cls === 'badge-success' ? 'badge--success' : b?.cls === 'badge-warning' ? 'badge--warning' : 'badge--danger'}`}>
                {b?.label || '—'}
              </span>
            </div>
            <div className="stat-lbl">Yield</div>
          </div>
        </div>

        <div className="alert-inline-success" style={{ width: '100%' }}>
          <Icon name="package" size={15} />
          Beef Tallow disponible : +{fmtMl(resultat.volume_tallow_ml)} ajoutés à l'inventaire matières
        </div>

        {resultat.yield_tallow_pct > 90 && (
          <div className="alert-inline-warning" style={{ width: '100%' }}>
            <Icon name="attention" size={15} />
            Yield élevé ({resultat.yield_tallow_pct}%) — veuillez vérifier la saisie du volume mesuré.
          </div>
        )}
        {resultat.yield_tallow_pct < 30 && (
          <div className="alert-inline-warning" style={{ width: '100%' }}>
            <Icon name="attention" size={15} />
            Yield faible ({resultat.yield_tallow_pct}%) — veuillez vérifier la saisie du volume mesuré.
          </div>
        )}

        {resultat.deductions?.length > 0 && (
          <div style={{ marginTop: 8, width: '100%', textAlign: 'left' }}>
            <h4 style={{ marginBottom: 10 }}>Inventaire déduit (matières premières)</h4>
            <table className="esc-table">
              <thead>
                <tr><th>Ingrédient</th><th>Déduit</th><th>Stock restant</th></tr>
              </thead>
              <tbody>
                {resultat.deductions.map(d => (
                  <tr key={d.matiere_id}>
                    <td>{d.nom}</td>
                    <td className="gold">−{d.quantite_deduite} {d.unite}</td>
                    <td className={d.statut === 'negatif' ? 'danger' : d.statut === 'bas' ? 'warning' : 'success'}>
                      {d.stock_apres} {d.unite}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <CoutRevient analytique={resultat.analytique} />
      </div>
    );
  }

  return (
    <form className="tallow-form" onSubmit={handleSubmit}>
      {/* Calculateur théorique */}
      {calculateur && (
        <div className="tallow-calc-box">
          <div className="calc-titre">Estimation théorique</div>
          <div className="calc-ligne">
            Pour <strong>{poidsCruKg} kg</strong> de gras · yield {calculateur.yield_theorique_pct}% →
            {' '}<strong className="gold">~{fmtMl(calculateur.volume_theo_ml)} attendus</strong>
          </div>
        </div>
      )}

      {/* Volume mesuré */}
      <div className="form-group">
        <label>Volume de tallow mesuré après filtration (ml) *</label>
        <input
          className="form-input"
          type="number"
          min="1"
          step="10"
          value={form.volume_tallow_ml}
          onChange={e => set('volume_tallow_ml', e.target.value)}
          placeholder="Ex : 5500"
          required
        />
      </div>

      {/* Yield en temps réel */}
      {volumeMl > 0 && (
        <div className="yield-panel">
          <div className="yield-row">
            <span>Volume mesuré</span>
            <strong>{fmtMl(volumeMl)}</strong>
          </div>
          {calculateur && (
            <div className="yield-row">
              <span>Volume théorique</span>
              <strong>{fmtMl(calculateur.volume_theo_ml)}</strong>
            </div>
          )}
          <div className="yield-row">
            <span>Yield calculé</span>
            {badge
              ? <span className={`badge ${badge.cls === 'badge-success' ? 'badge--success' : badge.cls === 'badge-warning' ? 'badge--warning' : 'badge--danger'}`}>{badge.label}</span>
              : <span className="muted">—</span>
            }
          </div>
          {(yieldReel > 90 || yieldReel < 30) && (
            <div className="yield-row" style={{ color: 'var(--warning)', fontSize: 12 }}>
              <Icon name="attention" size={13} /> Vérifier la saisie — yield hors plage normale (30–90%)
            </div>
          )}
        </div>
      )}

      <div className="form-grid">
        <div className="form-group">
          <label>Date de congélation (optionnel)</label>
          <DateInput
            className="form-input"
            value={form.date_congelation}
            onChange={v => set('date_congelation', v)}
          />
        </div>
        <div className="form-group">
          <label>Date d'expiration du produit</label>
          <DateInput
            className="form-input"
            value={form.date_expiration_produit}
            onChange={v => set('date_expiration_produit', v)}
          />
        </div>
      </div>

      <div className="form-group">
        <label>Température de conservation</label>
        <input
          className="form-input"
          value={form.temp_conservation}
          onChange={e => set('temp_conservation', e.target.value)}
          placeholder="Ex : ambiante, 4°C, congelé"
        />
      </div>

      <div className="form-group">
        <label>Incident (optionnel)</label>
        <textarea
          className="form-input"
          rows="2"
          value={form.incident}
          onChange={e => set('incident', e.target.value)}
          placeholder="Décrire tout incident ou observation particulière..."
        />
      </div>

      {/* Aperçu déduction */}
      {apercuDeduction?.deductions?.length > 0 && (
        <div style={{ marginTop: 4 }}>
          <h4 style={{ marginBottom: 10, fontSize: 13 }}>Aperçu déduction inventaire</h4>
          <table className="esc-table">
            <thead>
              <tr><th>Ingrédient</th><th>À déduire</th><th>Stock avant</th><th>Stock après</th></tr>
            </thead>
            <tbody>
              {apercuDeduction.deductions.map(d => (
                <tr key={d.matiere_id}>
                  <td>{d.nom}</td>
                  <td className="gold">−{d.quantite_deduite} {d.unite}</td>
                  <td className="muted">{d.stock_avant} {d.unite}</td>
                  <td className={d.statut === 'negatif' ? 'danger' : d.statut === 'bas' ? 'warning' : 'success'}>
                    {d.stock_apres} {d.unite}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {erreur && <div className="error-inline"><Icon name="erreur" size={15} />{erreur}</div>}

      <div className="form-actions">
        <button type="button" className="btn btn-ghost" onClick={onBack}><Icon name="retour" size={14} /> Retour</button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={loading || isFinalise || !form.volume_tallow_ml || volumeMl <= 0}
          title={isFinalise ? 'Batch déjà finalisé' : ''}
        >
          {loading ? 'Finalisation...' : isFinalise ? 'Déjà finalisé' : 'Finaliser le batch'}
        </button>
      </div>

      {isFinalise && (
        <div className="alert-inline-warning">
          <Icon name="attention" size={15} />
          Ce batch est déjà finalisé. La re-finalisation n'est pas permise.
        </div>
      )}
    </form>
  );
}

// ─── ÉTAPE 1 BIS — Édition batch existant Tallow ──
// Utilise EnteteBatch (champs communs avec auto-save) + poids_cru_kg en children.
// Les champs fournisseur, lot_fournisseur, date_achat_matiere, date_expiration_matiere,
// heure_fin sont maintenant tous exposés via EnteteBatch (traçabilité MAPAQ complète).

function EtapeBatchEditerTallow({ batchData, onUpdated }) {
  const batchObj   = batchData.form || batchData.batch || {};
  const isFinalise = batchObj.statut === 'finalise';

  // État local pour poids_cru_kg (champ spécifique tallow — auto-save sur blur)
  const [poidsCruKg, setPoidsCruKg] = useState(String(batchObj.poids_cru_kg || ''));

  const sauvegarderPoids = useCallback(async (valeur) => {
    if (!batchData.batchId) return;
    try {
      const updated = await api.put(`/tallow/batches/${batchData.batchId}`, {
        poids_cru_kg: parseFloat(valeur) || null,
      });
      if (onUpdated) onUpdated(updated);
    } catch {
      // Erreur silencieuse — EnteteBatch affiche les siennes
    }
  }, [batchData.batchId, onUpdated]); // eslint-disable-line

  return (
    <EnteteBatch
      apiBase="/tallow"
      batch={{ ...batchObj, id: batchData.batchId }}
      onUpdated={onUpdated}
    >
      {/* Champ spécifique tallow */}
      <div className="form-group">
        <label>Poids gras cru (kg)</label>
        <input
          className="form-input"
          type="number"
          min="0.1"
          step="0.1"
          value={poidsCruKg}
          disabled={isFinalise}
          onChange={e => setPoidsCruKg(e.target.value)}
          onBlur={() => sauvegarderPoids(poidsCruKg)}
          placeholder="ex : 10.5"
        />
        {isFinalise && (
          <span className="muted" style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
            <Icon name="verrou" size={12} /> Batch finalisé — le poids de gras est verrouillé.
          </span>
        )}
      </div>
    </EnteteBatch>
  );
}

// ─── SECTION STOCK TALLOW ─────────────────────

function SectionStockTallow({ stock }) {
  if (!stock) return null;
  return (
    <div className="card rise-in" style={{ marginTop: 24 }}>
      <div className="card-header">
        <div className="card-header-left">
          <span style={{ color: 'var(--muted)', display: 'flex', alignItems: 'center' }}>
            <Icon name="encaisse" size={16} />
          </span>
          <span className="card-title">Stock Beef Tallow disponible</span>
        </div>
      </div>
      <div className="stock-tallow-resume">
        <div className="stat-box">
          <div className="stat-val gold">{fmtMl(stock.stock_total_ml)}</div>
          <div className="stat-lbl">Stock total</div>
        </div>
        {stock.prix_unitaire > 0 && (
          <div className="stat-box">
            <div className="stat-val" style={{ fontSize: 15 }}>{stock.prix_unitaire.toFixed(4)} $/ml</div>
            <div className="stat-lbl">Prix indicatif</div>
          </div>
        )}
      </div>
      {stock.batches?.length > 0 && (
        <div style={{ marginTop: 16, overflowX: 'auto' }}>
          <div style={{ fontFamily: "'Cinzel', serif", fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
            Ordre FIFO — batches finalisés
          </div>
          <table className="esc-table">
            <thead>
              <tr>
                <th>Priorité</th>
                <th>Batch</th>
                <th>Date</th>
                <th>Volume produit</th>
                <th>Yield</th>
                <th>Coût/ml</th>
              </tr>
            </thead>
            <tbody>
              {stock.batches.map(b => (
                <tr key={b.batch_id} className={b.fifo_priorite === 1 ? 'fifo-prioritaire' : ''}>
                  <td>
                    {b.fifo_priorite === 1
                      ? <span className="badge badge--warning">1 — EN PREMIER</span>
                      : <span className="muted">{b.fifo_priorite}</span>}
                  </td>
                  <td style={{ fontWeight: 600 }}>{b.batch_nom}</td>
                  <td className="muted">{b.date_prod}</td>
                  <td>{fmtMl(b.volume_tallow_ml)}</td>
                  <td>{b.yield_tallow_pct ? `${b.yield_tallow_pct}%` : '—'}</td>
                  <td className="muted">{b.cout_par_ml ? `${b.cout_par_ml.toFixed(4)} $/ml` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── SECTION LISTE BATCHES ────────────────────

function SectionListeBatches({ onOuvrir, refreshTrigger }) {
  const [batches, setBatches]               = useState([]);
  const [loading, setLoading]               = useState(true);
  const [batchASupprimer, setBatchASupprimer] = useState(null);
  const [suppLoading, setSuppLoading]       = useState(false);
  const [toast, setToast]                   = useState('');
  const [erreur, setErreur]                 = useState('');

  const charger = useCallback(() => {
    setLoading(true);
    api.get('/tallow/batches')
      .then(setBatches)
      .catch(() => setBatches([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { charger(); }, [charger, refreshTrigger]);

  const afficherToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3500);
  };

  const supprimerBatch = async () => {
    if (!batchASupprimer) return;
    setSuppLoading(true);
    try {
      await api.delete(`/tallow/batches/${batchASupprimer.id}`);
      setBatches(prev => prev.filter(b => b.id !== batchASupprimer.id));
      afficherToast(`Batch ${batchASupprimer.nom} supprimé ✓`);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Erreur lors de la suppression');
    } finally {
      setSuppLoading(false);
      setBatchASupprimer(null);
    }
  };

  return (
    <div className="card rise-in" style={{ marginTop: 24 }}>
      {batchASupprimer && (
        <ConfirmDeleteModal
          nomBatch={batchASupprimer.nom}
          loading={suppLoading}
          onConfirm={supprimerBatch}
          onAnnuler={() => setBatchASupprimer(null)}
        />
      )}
      {toast && <div className="toast-success">{toast}</div>}

      <div className="card-header">
        <div className="card-header-left">
          <span style={{ color: 'var(--muted)', display: 'flex', alignItems: 'center' }}>
            <Icon name="liste" size={16} />
          </span>
          <span className="card-title">Tous les batches Tallow</span>
        </div>
      </div>
      {erreur && <div className="error-inline" style={{ marginBottom: 10 }}><Icon name="erreur" size={15} />{erreur}</div>}

      {loading ? (
        <div>
          <div className="skeleton" style={{ height: 40, marginBottom: 8, borderRadius: 'var(--radius-sm)' }} />
          <div className="skeleton" style={{ height: 40, marginBottom: 8, borderRadius: 'var(--radius-sm)' }} />
          <div className="skeleton" style={{ height: 40, borderRadius: 'var(--radius-sm)' }} />
        </div>
      ) : batches.length === 0 ? (
        <div className="empty-state">
          <Icon name="package" size={36} />
          <span className="empty-state__titre">Aucun batch tallow enregistré</span>
          <span>Cliquez sur « Nouveau batch tallow » pour commencer.</span>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="esc-table">
            <thead>
              <tr>
                <th>Batch</th>
                <th>Date</th>
                <th>Poids gras (kg)</th>
                <th>Volume produit</th>
                <th>Yield</th>
                <th>Statut</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {batches.map(b => {
                const yb = yieldBadge(b.yield_tallow_pct);
                const yieldCls = yb?.cls === 'badge-success' ? 'badge--success' : yb?.cls === 'badge-warning' ? 'badge--warning' : 'badge--danger';
                return (
                  <tr key={b.id}>
                    <td style={{ fontWeight: 600 }}>{b.nom}</td>
                    <td>{b.date_prod}</td>
                    <td>{b.poids_cru_kg ? `${b.poids_cru_kg} kg` : '—'}</td>
                    <td>{b.volume_tallow_ml ? fmtMl(b.volume_tallow_ml) : '—'}</td>
                    <td>
                      {b.yield_tallow_pct
                        ? <span className={`badge ${yieldCls}`}>{b.yield_tallow_pct}%</span>
                        : <span className="muted">—</span>}
                    </td>
                    <td>
                      <span className={`badge ${b.statut === 'finalise' ? 'badge--success' : 'badge--warning'}`}>
                        {b.statut === 'finalise' ? 'Finalisé' : 'En cours'}
                      </span>
                      {b.nb_tracabilite_manquante > 0 && (
                        <span className="badge badge--warning" style={{ marginLeft: 6 }}>
                          <Icon name="attention" size={11} /> {b.nb_tracabilite_manquante} sans facture
                        </span>
                      )}
                      {b.statut === 'finalise' && b.cout_par_unite > 0 && (
                        <span className="muted" style={{ fontSize: 11, marginLeft: 8 }}>
                          {b.cout_par_unite.toFixed(4)} $/ml
                        </span>
                      )}
                    </td>
                    <td className="td-actions">
                      <button className="btn btn-ghost btn--sm" onClick={() => onOuvrir(b)}>
                        Ouvrir
                      </button>
                      {b.statut === 'finalise' && (
                        <Link className="btn btn-ghost btn--sm" to={`/batches/${b.id}/tracabilite`}>
                          <Icon name="document" size={13} /> Traçabilité
                        </Link>
                      )}
                      <button
                        className="btn btn-danger btn--sm"
                        onClick={() => setBatchASupprimer(b)}
                        title="Supprimer ce batch"
                      >
                        <Icon name="supprimer" size={13} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── PAGE PRINCIPALE ──────────────────────────

export default function ProductionTallow() {
  const [mode, setMode]                       = useState('liste'); // 'liste' | 'edition'
  const [etape, setEtape]                     = useState(1);
  const [batchData, setBatchData]             = useState(null);
  const [recette, setRecette]                 = useState(null);
  const [stock, setStock]                     = useState(null);
  const [nbTracabiliteNonLies, setNbTracabiliteNonLies] = useState(0);
  const [batchReprise, setBatchReprise]       = useState(null);
  const [listeRefreshTrigger, setListeRefreshTrigger] = useState(0);
  const [modalRecette, setModalRecette]       = useState(false);
  const [batchAnalytique, setBatchAnalytique] = useState(null);

  const chargerDonnees = useCallback(() => {
    api.get('/tallow/recette').then(setRecette).catch(e => console.error('[tallow] recette:', e));
    api.get('/tallow/stock').then(setStock).catch(e => console.error('[tallow] stock:', e));
  }, []);

  useEffect(() => { chargerDonnees(); }, [chargerDonnees]);

  // Reprendre un batch en cours depuis la liste
  useEffect(() => {
    if (!batchReprise) return;
    const b = batchReprise;
    // Charger le détail frais depuis l'API
    api.get(`/tallow/batches/${b.id}`)
      .then(batchDetail => {
        setBatchData({
          batchId : b.id,
          form    : batchDetail,
          batch   : batchDetail,
        });
        setEtape(b.statut === 'finalise' ? 5 : 2);
        setMode('edition');
        setBatchReprise(null);
      })
      .catch(() => {
        setBatchData({ batchId: b.id, form: b, batch: b });
        setEtape(b.statut === 'finalise' ? 5 : 2);
        setMode('edition');
        setBatchReprise(null);
      });
  }, [batchReprise]); // eslint-disable-line

  function handleBatchCreated(data) {
    // EtapeBatch appelle onNext({ batchId, form }) sans clé `batch`.
    // On normalise ici pour que EtapeFonte/EtapeFumage puissent lire
    // batchData?.batch?.… comme lors de la reprise depuis la liste.
    setBatchData({ ...data, batch: data.batch ?? data.form });
    setListeRefreshTrigger(t => t + 1);
    setEtape(2);
  }

  function handleProduction() {
    setListeRefreshTrigger(t => t + 1);
    chargerDonnees();
  }

  function nouveauBatch() {
    setMode('edition');
    setEtape(1);
    setBatchData(null);
  }

  function retourListe() {
    setMode('liste');
    setEtape(1);
    setBatchData(null);
    setListeRefreshTrigger(t => t + 1);
    chargerDonnees();
  }

  function handleOuvrir(b) {
    setBatchReprise(b);
  }

  function handleStepClick(cible) {
    // Naviguer vers l'étape 1 affiche les données du batch courant,
    // pas un nouveau formulaire vide. Utiliser le bouton « + Nouveau batch » pour créer.
    if (!batchData && cible > 1) return;
    setEtape(cible);
  }

  return (
    <div className="production-tallow">
      {/* Modal analytique */}
      {batchAnalytique && (
        <div className="modal-overlay" onClick={() => setBatchAnalytique(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 860, maxHeight: '90vh', overflowY: 'auto' }}>
            <BatchAnalytiquePanel batch={batchAnalytique} onFermer={() => setBatchAnalytique(null)} />
          </div>
        </div>
      )}

      {/* Modal recette */}
      {modalRecette && (
        <ModalRecette
          recette={recette}
          onFermer={() => setModalRecette(false)}
          onSauvegarde={r => { setRecette(r); setModalRecette(false); }}
        />
      )}

      {/* En-tête */}
      <div className="page-header">
        <div className="page-header-text">
          <h1 className="page-title">
            <span className="gold">Beef Tallow</span> — Suif de bœuf fumé
          </h1>
          <p className="page-subtitle">
            Usage interne · Inventaire en ml
            {recette && ` · Yield théorique : ${recette.yield_theorique_pct}%`}
          </p>
        </div>
        <button className="btn btn-ghost" onClick={() => setModalRecette(true)}>
          <Icon name="parametres" size={15} /> Modifier la recette
        </button>
      </div>

      {/* Bandeau recette */}
      {recette && (
        <div className="tallow-recette-bandeau">
          <span>Yield théorique : <strong className="gold">{recette.yield_theorique_pct}%</strong></span>
          <span className="muted">·</span>
          <span>Densité : <strong>{recette.densite_g_par_ml} g/ml</strong></span>
          {recette.prix_indicatif_par_ml > 0 && (
            <>
              <span className="muted">·</span>
              <span>Prix indicatif : <strong>{recette.prix_indicatif_par_ml.toFixed(4)} $/ml</strong></span>
            </>
          )}
          {recette.notes && (
            <>
              <span className="muted">·</span>
              <span className="muted" style={{ fontStyle: 'italic', fontSize: 12 }}>{recette.notes}</span>
            </>
          )}
        </div>
      )}

      {/* MODE LISTE — pas de stepper */}
      {mode === 'liste' && (
        <>
          <div className="form-actions" style={{ marginBottom: 12 }}>
            <button className="btn btn-primary" onClick={nouveauBatch}>
              <Icon name="ajouter" size={15} /> Nouveau batch tallow
            </button>
          </div>
          <SectionStockTallow stock={stock} />
          <SectionListeBatches onOuvrir={handleOuvrir} refreshTrigger={listeRefreshTrigger} />
        </>
      )}

      {/* MODE ÉDITION — stepper + étapes */}
      {mode === 'edition' && (
        <>
          <div style={{ marginBottom: 12, display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost btn--sm" onClick={retourListe}>
              <Icon name="retour" size={13} /> Retour aux batches
            </button>
            {batchData && (
              <button className="btn btn-ghost btn--sm" onClick={nouveauBatch}>
                <Icon name="ajouter" size={13} /> Nouveau batch
              </button>
            )}
          </div>
          <div className="card" style={{ marginBottom: 0 }}>
            <Stepper
              etape={etape}
              nbTracabiliteNonLies={nbTracabiliteNonLies}
              onStepClick={handleStepClick}
              hasBatch={!!batchData}
            />

            {/* En-tête batch courant */}
            {batchData && (
              <div className="tallow-batch-header">
                <Icon name="brisket" size={15} color="var(--muted)" />
                <span style={{ fontWeight: 600 }}>{batchData.form.nom || batchData.batch?.nom}</span>
                <span className="muted">·</span>
                <span className="muted">{batchData.form.poids_cru_kg || batchData.batch?.poids_cru_kg} kg de gras</span>
                {batchData.form?.statut === 'finalise' && (
                  <span className="badge badge--success" style={{ marginLeft: 8 }}>Finalisé</span>
                )}
                {(batchData.form?.statut === 'en_cours' || !batchData.form?.statut) && (
                  <span className="badge badge--warning" style={{ marginLeft: 8 }}>En cours</span>
                )}
              </div>
            )}

            {etape === 1 && (batchData
              ? <EtapeBatchEditerTallow batchData={batchData} onUpdated={updated => setBatchData(prev => ({ ...prev, form: updated, batch: updated }))} />
              : <EtapeBatch onNext={handleBatchCreated} />
            )}

            {etape === 2 && batchData && (
              <EtapeTracabilite
                batchId={batchData.batchId}
                batchNom={batchData.form?.nom || batchData.batch?.nom}
                batchStatut={batchData.form?.statut || batchData.batch?.statut}
                onNext={() => setEtape(3)}
                onBack={() => setEtape(1)}
                onNbNonLiesChange={setNbTracabiliteNonLies}
              />
            )}

            {etape === 3 && batchData && (
              <EtapeFonte
                batchData={batchData}
                onNext={() => setEtape(4)}
                onBack={() => setEtape(2)}
              />
            )}

            {etape === 4 && batchData && (
              <EtapeFumage
                batchData={batchData}
                onNext={() => setEtape(5)}
                onBack={() => setEtape(3)}
              />
            )}

            {etape === 5 && batchData && (
              <EtapeFinalisation
                batchData={batchData}
                recette={recette}
                onSuccess={handleProduction}
                onBack={() => setEtape(4)}
              />
            )}

            {/* Boutons après finalisation */}
            {etape === 5 && (
              <div className="form-actions" style={{ marginTop: 16, gap: 8, display: 'flex' }}>
                <button className="btn btn-ghost" onClick={retourListe}>
                  <Icon name="retour" size={14} /> Retour aux batches
                </button>
                <button className="btn btn-primary" onClick={nouveauBatch}>
                  <Icon name="ajouter" size={14} /> Nouveau batch
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
